import { t } from 'i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getSeriesUrlByDataKey } from '../../../domain/dashboardChart/dashboardChartUseCase';
import cn from '../../../utils/cn';

import styles from './ChartBar.module.css';

const CHART_HEIGHT = 280;

/**
 * Open the series URL when a legend item is clicked.
 * @param {{ dataKey?: string | number | Function }} payload - Recharts legend payload
 * @param {MappedChartSeries[]} series - Mapped series
 * @returns {void}
 */
function openLegendUrl(payload, series) {
  const dataKey = typeof payload?.dataKey === 'string' ? payload.dataKey : undefined;
  const url = getSeriesUrlByDataKey(series, dataKey);
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Bar chart for dashboard (Recharts BarChart).
 * @param {object} props
 * @param {MappedChartRow[]} props.rows - Recharts rows
 * @param {MappedChartSeries[]} props.series - Series
 * @param {MappedChartReferenceLine[]} [props.referenceLines] - Reference lines
 * @param {MappedChartLegend | null} [props.legend] - Legend config, null to hide
 * @param {'horizontal' | 'vertical'} [props.layout='horizontal'] - Recharts layout
 * @param {boolean} [props.stacked=false] - Stack series
 * @param {string[] | null} [props.categoryColors] - Per-category colors (single series)
 * @returns {JSX.Element}
 */
function ChartBar({
  rows,
  series,
  referenceLines = [],
  legend = null,
  layout = 'horizontal',
  stacked = false,
  categoryColors = null,
}) {
  if (!rows?.length || !series?.length) {
    return <p className={styles['chart-bar-empty']}>{t('dashboard.emptyChart')}</p>;
  }

  const isVertical = layout === 'vertical';

  return (
    <div
      className={cn([
        styles['chart-bar'],
        legend?.layout === 'horizontal' && styles['chart-bar-legend-horizontal'],
      ])}
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart
          data={rows}
          layout={layout}
          margin={{
            top: 8,
            right: 16,
            left: 0,
            bottom: 8,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--neutral-200)" />
          <XAxis
            type={isVertical ? 'number' : 'category'}
            dataKey={isVertical ? undefined : 'category'}
            tick={{ fill: 'var(--neutral-600)', fontSize: 12 }}
          />
          <YAxis
            type={isVertical ? 'category' : 'number'}
            dataKey={isVertical ? 'category' : undefined}
            tick={{ fill: 'var(--neutral-600)', fontSize: 12 }}
            width={isVertical ? 100 : undefined}
          />
          <Tooltip />
          {legend ? (
            <Legend
              layout={legend.layout}
              align={legend.align}
              verticalAlign={legend.verticalAlign}
              itemSorter={null}
              onClick={(payload) => openLegendUrl(payload, series)}
            />
          ) : null}
          {series.map((serie) => (
            <Bar
              key={serie.dataKey}
              dataKey={serie.dataKey}
              name={serie.name}
              fill={serie.color}
              stackId={stacked ? 'stack' : undefined}
            >
              {categoryColors
                ? categoryColors.map((color, index) => (
                  <Cell
                    key={`${serie.dataKey}-${rows[index]?.category ?? index}`}
                    fill={color}
                  />
                ))
                : null}
            </Bar>
          ))}
          {referenceLines.map((line) => (
            isVertical ? (
              <ReferenceLine
                key={`${line.label}-${line.value}`}
                x={line.value}
                stroke={line.color}
                strokeDasharray="4 4"
                label={{ value: line.label, fill: line.color, position: 'insideTopRight' }}
              />
            ) : (
              <ReferenceLine
                key={`${line.label}-${line.value}`}
                y={line.value}
                stroke={line.color}
                strokeDasharray="4 4"
                label={{ value: line.label, fill: line.color, position: 'insideTopRight' }}
              />
            )
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ChartBar;
